import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "/assets/hero.webp";

const lines = [
    { text: "Specialist Motorhome", italic: false },
    { text: "Valeting &", italic: false },
    { text: "Ceramic Coatings", italic: false },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden pv-noise" data-testid="hero-section">
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img src={HERO_IMG} alt="A freshly valeted motorhome by Planet Valet" className="w-full h-full object-cover object-center contrast-[1.04] saturate-[1.08]" />
                <div className="absolute inset-0 bg-indigo-950/40" />
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/85 via-indigo-950/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-transparent to-indigo-950/40" />
            </motion.div>
            <div className="absolute inset-0 pv-stars opacity-60" />

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pb-24 sm:pb-32 w-full">
                <h1 className="font-serif-display font-medium tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-white">
                    {lines.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${line.italic ? "italic bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent pr-1" : ""}`}
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.55 + i * 0.18 }}
                                data-testid={`hero-line-${i + 1}`}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.4 }}
                    className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed font-medium text-white/90"
                    data-testid="hero-subcopy"
                >
                    Planet Valet is an established motorhome business based at Holton Heath Industrial
                    Estate in Poole that has been providing over 20 years service to both the trade and
                    public alike. We are specialists in machine polishing which will restore the
                    motorhome's paintwork to its original condition, as well as being able to offer
                    Ceramic Coatings to enhance and protect the paintwork for up to 5 years.
                    <span className="block mt-4">
                        We can tailor any valet to suit — from a basic wash to a full valet. Just get
                        in touch and we can discuss the different options.
                    </span>
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.6 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
                        className="bg-white hover:bg-blue-50 text-indigo-950 text-sm tracking-[0.12em] uppercase font-bold px-8 py-4 transition-colors duration-300"
                        data-testid="hero-book-button"
                    >
                        Book a valet
                    </motion.button>
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                        className="border border-white/30 hover:border-blue-300 hover:text-blue-300 text-white text-sm tracking-[0.12em] uppercase font-semibold px-8 py-4 transition-colors duration-300"
                        data-testid="hero-services-button"
                    >
                        View services
                    </motion.button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 1 }}
                className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-3 text-white/60 text-xs tracking-[0.25em] uppercase"
            >
                Scroll <ArrowDown className="w-4 h-4 animate-bounce" />
            </motion.div>
        </section>
    );
};
